/**
 * useUserAvatar — resolve the current user's avatar to a DOM-safe URL that works
 * in every connection mode (local browser, cloud WebRTC, Electron).
 *
 * The hub exposes the avatar at the public endpoint `GET /api/users/:id/image`,
 * but that endpoint can only be reached through the API layer (`api` / `apiFetch`),
 * which routes requests over the WebRTC data channel in cloud mode and through the
 * Electron IPC bridge in Electron. A raw relative URL in an <img> tag cannot cross
 * those transports, which is why the avatar previously degraded to the "U" initials
 * in cloud / Electron.
 *
 * We therefore fetch the avatar as a Blob through `api.get(..., { responseType: 'blob' })`
 * and mint an object URL, mirroring the existing system-branding image pattern
 * (stores/system.ts) and the image-assets cache pattern (stores/imageAssets.ts).
 *
 * Cloud fallback: when the hub user has no avatar (or the hub user is not resolved
 * yet, e.g. right after a cloud WebRTC connect), fall back to the cloud account's
 * image (`authStore.cloudUser.image`), which is a plain cloud-hosted URL.
 */

import { ref, watch } from 'vue';
import api from '../services/api';
import { useAuthStore } from '../stores/auth';

// Module-level cache: userId -> object URL. Dedupes concurrent fetches so the
// global header and the user drawer share one request/blob.
const cache = new Map<string, string>();
const inFlight = new Map<string, Promise<string | null>>();

/**
 * Resolve a user's avatar (by user id) to a DOM-safe object URL, or null when
 * the user has no avatar. Fetches through the routed API layer so it works in
 * local, cloud WebRTC and Electron modes. Results are cached per user id.
 */
export async function fetchAvatarUrl(userId: string, force = false): Promise<string | null> {
  if (!force) {
    const cached = cache.get(userId);
    if (cached) return cached;
    if (inFlight.has(userId)) return inFlight.get(userId)!;
  }

  const request = api
    .get(`/users/${encodeURIComponent(userId)}/image`, { responseType: 'blob' })
    .then((response: { data?: Blob }) => {
      const blob = response?.data;
      // The hub 404s cleanly when no avatar exists; some proxies may return an
      // empty/JSON body instead — treat all of those as "no avatar".
      if (!blob || !blob.size || (blob.type && blob.type.includes('json'))) {
        return null;
      }
      const url = URL.createObjectURL(blob);
      const previous = cache.get(userId);
      if (previous && previous !== url) URL.revokeObjectURL(previous);
      cache.set(userId, url);
      return url;
    })
    .catch(() => {
      // 404 / network error -> no avatar available.
      return null;
    })
    .finally(() => {
      inFlight.delete(userId);
    });

  inFlight.set(userId, request);
  return request;
}

function revokeAvatarUrl(userId: string): void {
  const url = cache.get(userId);
  if (url) {
    URL.revokeObjectURL(url);
    cache.delete(userId);
  }
}

export function useUserAvatar() {
  const authStore = useAuthStore();
  const url = ref<string | null>(null);
  const error = ref(false);
  const activeUserId = ref<string | null>(null);

  async function refresh(force = false): Promise<void> {
    const hubUser = authStore.user;
    const hubUserId = hubUser?.id ?? null;
    activeUserId.value = hubUserId;

    // 1) Hub avatar — fetched as a blob through the routed API layer.
    if (hubUserId) {
      const avatarUrl = await fetchAvatarUrl(hubUserId, force);
      // Ignore stale resolutions if the auth state changed mid-fetch.
      if (activeUserId.value !== hubUserId) return;
      if (avatarUrl) {
        url.value = avatarUrl;
        error.value = false;
        return;
      }
      // No hub avatar — fall back to the cloud account image if present.
      if (authStore.cloudUser?.image) {
        url.value = authStore.cloudUser.image;
        error.value = false;
        return;
      }
      url.value = null;
      error.value = true;
      return;
    }

    // 2) No hub user yet (cloud mode before /users/me resolves) — use the
    //    cloud account image directly.
    if (authStore.cloudUser?.image) {
      url.value = authStore.cloudUser.image;
      error.value = false;
      return;
    }

    url.value = null;
    error.value = true;
  }

  // Re-resolve whenever the hub user or cloud user changes (login, logout,
  // hub switch, cloud connect resolving the hub user, etc.).
  watch(
    () => [authStore.user, authStore.cloudUser] as const,
    () => {
      void refresh();
    },
  );

  void refresh();

  return { url, error, refresh };
}

export { revokeAvatarUrl };
